import {
  concatMap,
  filter,
  map,
  mergeMap,
  mergeScan,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { from, merge, Observable } from 'rxjs';
import { featureOperations, Features } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import {
  toggleFavorite,
  toggleModelPreview,
  updateCurrentUser,
  updateAssetsBrowserPerPageSettings,
  updateHomepageViewpointsState,
  updatePersonalSetting,
  setPersonalSetting,
  setPersonalSettingInBackground,
  acceptTermsAndPrivacyPolicy,
  receivedUpdatedCurrentUser,
  viewStyleUsed,
  type UpdatePersonalSettingUnion,
} from './actions';
import {
  type ActionStream,
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import { currentUser } from 'models/currentUser';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { logError } from '@ardoq/logging';
import { alert } from '@ardoq/modal';
import { loadedCurrentUser$ } from './currentUser$';
import {
  isFavorited,
  type PersonalSettings,
  type PersonalSettingWrite,
  toPersonalSettingWrite,
  withFavorite,
  withPersonalSetting,
  withReappliedPersonalSetting,
  withReplacedPersonalSetting,
  withRolledBackPersonalSetting,
} from './personalSettingOperations';
import {
  APICurrentUser,
  PersonalSetting,
  PersonalSettingMap,
} from '@ardoq/api-types';
import { toStoredViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { isViewEligibleForNewAssets } from 'views/environmentals/restrictedViews';
import { viewExportSucceeded } from '@ardoq/export/state';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { viewpointModeViews$, workspaceModeViews$ } from 'views/modeViews$';
import { toCurrentModeViews } from 'views/environmentals/viewSettings/utils';
import {
  type ApiResponse,
  FavoriteAction,
  type FavoritePayload,
  handleError,
  userApi,
} from '@ardoq/api';
import { ArdoqError, isArdoqError } from '@ardoq/common-helpers';
import {
  submittedEmailPreferences,
  submitEmailPreferencesFailed,
  submitEmailPreferencesSucceeded,
} from '../../admin/user/EmailPreferencesView/actions';

const logErrorAndAlertFavoriteFailed = (error: ArdoqError) => {
  logError(error, 'Favorite asset failed');
  alert({
    title: 'Error',
    subtitle: 'Sorry, saving your favorites failed',
  });
};

const logErrorAndAlertSettingsFailed = (error: ArdoqError) => {
  logError(error, 'Saving user settings failed');
  alert({
    title: 'Error',
    subtitle: 'Sorry, saving your settings failed',
  });
};

const applySettings = (settings: APICurrentUser['settings']) => {
  currentUser.set('settings', settings);
  currentUser.trigger('sync');
};

const handleAcceptTermsAndPrivacyPolicy = routine(
  ofType(acceptTermsAndPrivacyPolicy),
  extractPayload(),
  mergeMap(userApi.acceptTerms),
  handleError(logError),
  // CurrentUser is not updated via websocket
  tap(() => currentUser.fetch())
);

const handleUpdateCurrentUser = routine(
  ofType(updateCurrentUser),
  tap(() => currentUser.fetch())
);

/**
 * Received an updated current user object that should replace the current one.
 *
 * WARNING:
 * Be careful, few endpoints actually return APICurrentUser with all fields and
 * it can be difficult to spot if something is missing. Only use this action if
 * you know you have a full APICurrentUser object.
 */
const handleSetCurrentUser = routine(
  ofType(receivedUpdatedCurrentUser),
  extractPayload(),
  tap((user: APICurrentUser) => {
    currentUserInterface.setCurrentUserAttributes(structuredClone(user));
  })
);

type QueuedPersonalSettingWrite = {
  personalSetting: keyof PersonalSettingMap;
  withWrite: (settings: PersonalSettings) => PersonalSettings;
  persist: (user: APICurrentUser) => ApiResponse<APICurrentUser>;
  withRollback: (
    settings: PersonalSettings,
    confirmedSettings: PersonalSettings,
    write: PersonalSettingWrite
  ) => PersonalSettings;
  onFailure: (error: ArdoqError) => void;
  onSuccess?: () => void;
};

const persistLatestSettings = (user: APICurrentUser) =>
  userApi.updateSettings({ userId: user._id, settings: user.settings });

const settingsPersistence = {
  persist: persistLatestSettings,
  withRollback: withRolledBackPersonalSetting,
};

const toQueuedPersonalSettingUpdate = (
  update: UpdatePersonalSettingUnion
): QueuedPersonalSettingWrite => ({
  ...settingsPersistence,
  personalSetting: update.personalSetting,
  withWrite: settings => withPersonalSetting(settings, update),
  onFailure: logErrorAndAlertSettingsFailed,
});

const toQueuedPersonalSettingReplacement = (
  update: UpdatePersonalSettingUnion
): QueuedPersonalSettingWrite => ({
  ...settingsPersistence,
  personalSetting: update.personalSetting,
  withWrite: settings => withReplacedPersonalSetting(settings, update),
  onFailure: logErrorAndAlertSettingsFailed,
});

const toQueuedBackgroundPersonalSettingReplacement = (
  update: UpdatePersonalSettingUnion
): QueuedPersonalSettingWrite => ({
  ...toQueuedPersonalSettingReplacement(update),
  onFailure: logError,
});

const toToggledModelPreviewUpdate = ([, user]: [
  unknown,
  APICurrentUser,
]): UpdatePersonalSettingUnion => ({
  personalSetting: PersonalSetting.HIDE_MODEL_PREVIEW,
  newValue: !user.settings[PersonalSetting.HIDE_MODEL_PREVIEW],
});

const toFavoriteWrite = ([payload, user]: [
  FavoritePayload,
  APICurrentUser,
]): QueuedPersonalSettingWrite => {
  const favorited = !isFavorited(user.settings, payload);
  return {
    personalSetting: PersonalSetting.FAVORITE,
    withWrite: settings => withFavorite(settings, payload, favorited),
    persist: ({ _id }) =>
      userApi.updateFavorite({
        action: favorited ? FavoriteAction.ADD : FavoriteAction.REMOVE,
        userId: _id,
        payload,
      }),
    withRollback: (settings, confirmedSettings) =>
      isFavorited(settings, payload) === favorited
        ? withFavorite(
            settings,
            payload,
            isFavorited(confirmedSettings, payload)
          )
        : settings,
    onFailure: logErrorAndAlertFavoriteFailed,
  };
};

const toAssetsBrowserPerPageUpdate = ({
  appModule,
  perPage,
}: {
  appModule: string;
  perPage: number;
}): UpdatePersonalSettingUnion => ({
  personalSetting: PersonalSetting.ASSETS_BROWSER_PER_PAGE,
  newValue: { [appModule]: perPage },
});

const toHomepageViewpointsStateUpdate = (
  viewpointsState: NonNullable<
    PersonalSettings[PersonalSetting.HOMEPAGE_VIEWPOINTS_STATE]
  >
): UpdatePersonalSettingUnion => ({
  personalSetting: PersonalSetting.HOMEPAGE_VIEWPOINTS_STATE,
  newValue: viewpointsState,
});

const toViewStyleUsedWrite = (
  viewId: PersonalSettingMap[PersonalSetting.RECENTLY_USED_VIEW_STYLE]
): QueuedPersonalSettingWrite => ({
  ...settingsPersistence,
  personalSetting: PersonalSetting.RECENTLY_USED_VIEW_STYLE,
  withWrite: settings => ({
    ...settings,
    [PersonalSetting.RECENTLY_USED_VIEW_STYLE]: viewId,
  }),
  onFailure: logError,
});

const toEmailDigestWrite = (
  digestSettings: PersonalSettingMap[PersonalSetting.DEFAULT]
): QueuedPersonalSettingWrite => ({
  ...settingsPersistence,
  personalSetting: PersonalSetting.DEFAULT,
  withWrite: settings => ({
    ...settings,
    [PersonalSetting.DEFAULT]: digestSettings,
  }),
  onFailure: error => {
    logError(error);
    dispatchAction(submitEmailPreferencesFailed());
  },
  onSuccess: () => dispatchAction(submitEmailPreferencesSucceeded()),
});

const emailDigestWrite$ = (action$: ActionStream) =>
  action$.pipe(
    ofType(submittedEmailPreferences),
    extractPayload(),
    switchMap(({ userId, consent, digestSettings }) =>
      from(userApi.updateConsent(userId, consent)).pipe(
        handleError(error => {
          logError(error);
          dispatchAction(submitEmailPreferencesFailed());
        }),
        map(() => toEmailDigestWrite(digestSettings))
      )
    )
  );

const viewStyleUsedWrite$ = (action$: ActionStream) =>
  action$.pipe(
    ofType(viewStyleUsed, viewExportSucceeded),
    extractPayload(),
    map(toStoredViewpointViewId),
    withLatestFrom(
      isViewpointMode$,
      viewpointModeViews$,
      workspaceModeViews$,
      featureContext$
    ),
    filter(
      ([
        viewId,
        { isViewpointMode },
        viewpointModeViews,
        workspaceModeViews,
        featureContext,
      ]) =>
        isViewEligibleForNewAssets(
          toCurrentModeViews(
            isViewpointMode,
            viewpointModeViews,
            workspaceModeViews
          ).byIds[viewId]
        ) &&
        featureOperations.hasFeature(
          featureContext,
          Features.RECENTLY_USED_VIEW_STYLES
        )
    ),
    map(([viewId]) => toViewStyleUsedWrite(viewId))
  );

const queuedPersonalSettingWrite$ = (action$: ActionStream) =>
  merge(
    action$.pipe(
      ofType(updatePersonalSetting),
      extractPayload(),
      map(toQueuedPersonalSettingUpdate)
    ),
    action$.pipe(
      ofType(setPersonalSetting),
      extractPayload(),
      map(toQueuedPersonalSettingReplacement)
    ),
    action$.pipe(
      ofType(setPersonalSettingInBackground),
      extractPayload(),
      map(toQueuedBackgroundPersonalSettingReplacement)
    ),
    action$.pipe(
      ofType(toggleModelPreview),
      withLatestFrom(loadedCurrentUser$),
      map(toToggledModelPreviewUpdate),
      map(toQueuedPersonalSettingUpdate)
    ),
    action$.pipe(
      ofType(updateAssetsBrowserPerPageSettings),
      extractPayload(),
      map(toAssetsBrowserPerPageUpdate),
      map(toQueuedPersonalSettingUpdate)
    ),
    action$.pipe(
      ofType(updateHomepageViewpointsState),
      extractPayload(),
      map(toHomepageViewpointsStateUpdate),
      map(toQueuedPersonalSettingUpdate)
    ),
    action$.pipe(
      ofType(toggleFavorite),
      extractPayload(),
      withLatestFrom(loadedCurrentUser$),
      map(toFavoriteWrite)
    ),
    viewStyleUsedWrite$(action$),
    emailDigestWrite$(action$)
  );

const toOptimisticPersonalSettingWrite = ([queued, user]: [
  QueuedPersonalSettingWrite,
  APICurrentUser,
]) => {
  const settings = queued.withWrite(user.settings);
  return {
    queued,
    settings,
    settingsBeforeWrite: user.settings,
    write: toPersonalSettingWrite(settings, queued.personalSetting),
  };
};

type OptimisticPersonalSettingWrite = ReturnType<
  typeof toOptimisticPersonalSettingWrite
>;

const toReappliedUser = (
  user: APICurrentUser,
  confirmedSettings: PersonalSettings,
  { queued, write }: OptimisticPersonalSettingWrite
): APICurrentUser => {
  const settings = withReappliedPersonalSetting(
    user.settings,
    confirmedSettings,
    write,
    queued.withWrite
  );
  if (settings === user.settings) return user;
  return { ...user, settings };
};

const applyReappliedSettings = (
  user: APICurrentUser,
  reappliedUser: APICurrentUser
) => {
  if (reappliedUser !== user) applySettings(reappliedUser.settings);
};

const settlePersonalSettingWrite = (
  confirmedSettings: PersonalSettings,
  optimisticWrite: OptimisticPersonalSettingWrite,
  result: APICurrentUser | ArdoqError,
  user: APICurrentUser
): PersonalSettings => {
  const { queued, write } = optimisticWrite;
  if (!isArdoqError(result)) {
    queued.onSuccess?.();
    applyReappliedSettings(
      user,
      toReappliedUser(user, confirmedSettings, optimisticWrite)
    );
    return result.settings;
  }
  queued.onFailure(result);
  applySettings(queued.withRollback(user.settings, confirmedSettings, write));
  return confirmedSettings;
};

const persistPersonalSettingWrite = (
  lastConfirmedSettings: PersonalSettings | null,
  optimisticWrite: OptimisticPersonalSettingWrite
): Observable<PersonalSettings> => {
  const confirmedSettings =
    lastConfirmedSettings ?? optimisticWrite.settingsBeforeWrite;
  return loadedCurrentUser$.pipe(
    take(1),
    map(user => ({
      user,
      reappliedUser: toReappliedUser(user, confirmedSettings, optimisticWrite),
    })),
    tap(({ user, reappliedUser }) =>
      applyReappliedSettings(user, reappliedUser)
    ),
    concatMap(({ reappliedUser }) =>
      optimisticWrite.queued.persist(reappliedUser)
    ),
    withLatestFrom(loadedCurrentUser$),
    map(([result, user]) =>
      settlePersonalSettingWrite(
        confirmedSettings,
        optimisticWrite,
        result,
        user
      )
    )
  );
};

const handlePersonalSettingWrites = routine(
  queuedPersonalSettingWrite$,
  withLatestFrom(loadedCurrentUser$),
  map(toOptimisticPersonalSettingWrite),
  tap(({ settings }) => applySettings(settings)),
  mergeScan<OptimisticPersonalSettingWrite, PersonalSettings | null>(
    persistPersonalSettingWrite,
    null,
    1
  )
);

export const currentUserRoutines = collectRoutines(
  handleAcceptTermsAndPrivacyPolicy,
  handleUpdateCurrentUser,
  handleSetCurrentUser,
  handlePersonalSettingWrites
);
